import React from 'react';
import Icon from './Icon';
import { formatWhen, platformLabel, shortReason } from '../utils/publishing';
import './publish.css';

// Where a planned post stands with publishing: one fixed-height line per job
// (and per link the post was published to). Nothing when it has neither.

// Posted now, or its time has come: the worker picks it up within a minute or two
export const isDue = (job, now = Date.now()) => !job.scheduledAt || new Date(job.scheduledAt).getTime() <= now + 60 * 1000;

const ORDER = { failed: 0, running: 1, processing: 1, queued: 2, published: 3 };

const PublishStatus = ({ jobs = [], published = [], busy = {}, onCancel, onRetry }) => {
    const publishedJobIds = published.map(item => item.jobId).filter(Boolean);
    const shown = jobs
        .filter(job => job.status !== 'cancelled' && !(job.status === 'published' && publishedJobIds.includes(job.id)))
        .sort((a, b) => (ORDER[a.status] - ORDER[b.status]) || String(a.scheduledAt).localeCompare(String(b.scheduledAt)));
    if (!shown.length && !published.length) return null;

    const renderJob = (job) => {
        const platform = platformLabel(job.platform);
        const action = busy[job.id];
        if (job.status === 'queued') {
            return (
                <li key={job.id} className="ls-publish-status-line is-queued">
                    <Icon name="schedule" size={16} />
                    <span className="ls-publish-status-text">
                        {isDue(job) ? 'Posting in a minute or two' : `Scheduled ${formatWhen(job.scheduledAt)}`} · {platform}
                    </span>
                    {onCancel && (
                        <button type="button" className="ls-link ls-publish-status-action" onClick={() => onCancel(job)} disabled={Boolean(action)}>
                            {action === 'cancel' ? <span className="ls-spinner" /> : null}Cancel
                        </button>
                    )}
                </li>
            );
        }
        if (job.status === 'running' || job.status === 'processing') {
            return (
                <li key={job.id} className="ls-publish-status-line is-active" role="status">
                    <span className="ls-spinner" />
                    <span className="ls-publish-status-text">Publishing… · {platform}</span>
                </li>
            );
        }
        if (job.status === 'published') {
            return (
                <li key={job.id} className="ls-publish-status-line is-published">
                    <Icon name="check_circle" size={16} />
                    <span className="ls-publish-status-text">
                        {job.externalUrl
                            ? <a className="ls-link" href={job.externalUrl} target="_blank" rel="noopener noreferrer">Published<Icon name="north_east" size={14} /></a>
                            : 'Published'}
                        {' '}· {platform}
                    </span>
                </li>
            );
        }
        return (
            <li key={job.id} className="ls-publish-status-line is-failed">
                <Icon name="error" size={16} />
                <span className="ls-publish-status-text" title={job.lastError || undefined}>
                    Failed: {shortReason(job.lastError)} · {platform}
                </span>
                {onRetry && (
                    <button type="button" className="ls-link ls-publish-status-action" onClick={() => onRetry(job)} disabled={Boolean(action)}>
                        {action === 'retry' ? <span className="ls-spinner" /> : null}Retry
                    </button>
                )}
            </li>
        );
    };

    return (
        <ul className="ls-publish-status" aria-label="Publishing">
            {published.map((item, index) => (
                <li key={`p-${item.jobId || index}`} className="ls-publish-status-line is-published">
                    <Icon name="check_circle" size={16} />
                    <span className="ls-publish-status-text">
                        <a className="ls-link" href={item.url} target="_blank" rel="noopener noreferrer">
                            Published<Icon name="north_east" size={14} />
                        </a>
                        {' '}· {platformLabel(item.platform)}{item.at ? ` · ${formatWhen(item.at)}` : ''}
                    </span>
                </li>
            ))}
            {shown.map(renderJob)}
        </ul>
    );
};

export default PublishStatus;
